// Turn a failed mutation (Apollo/GraphQL error) into a readable message.
// genesis surfaces *validation* errors cleanly via ValidateXResult unions;
// unexpected server exceptions (e.g. a unique-name violation that isn't
// modelled as a Validate result) bubble up as opaque GraphQL errors - this
// maps the common ones to something a user can act on. User-facing copy is
// never hardcoded here: callers pass i18n-resolved fallback/duplicate strings.
//
// Shared helper so features don't each show the raw "non-null type … bubble"
// spec text. Extend the pattern list as more backend quirks surface.

type ValidationIssueLike = {
  field?: string | null;
  error?: string | null;
  message?: string | null;
};

type ValidationResultLike = {
  __typename?: string | null;
  issues?: readonly ValidationIssueLike[] | null;
};

const NON_NULL_BUBBLE_HINTS = [
  "was declared as a non null type",
  "wrongly returned a null value",
  "the non-nullable type is",
];

type GraphQlErrorLike = {
  message?: string | null;
  extensions?: Readonly<Record<string, unknown>> | null;
};

function graphQlErrors(e: unknown): readonly GraphQlErrorLike[] {
  if (CombinedGraphQLErrors.is(e)) return e.errors;
  if (e && typeof e === "object") {
    // Apollo Client 3 compatibility for errors crossing an older integration
    // boundary. Apollo Client 4 itself uses CombinedGraphQLErrors above.
    const errors = (e as { graphQLErrors?: readonly GraphQlErrorLike[] }).graphQLErrors;
    if (Array.isArray(errors)) return errors;
  }
  return [];
}

export function graphQlErrorExtensions(
  error: unknown,
  code: string,
): Readonly<Record<string, unknown>> | null {
  for (const graphQlError of graphQlErrors(error)) {
    if (graphQlError.extensions?.["code"] === code) {
      return graphQlError.extensions;
    }
  }
  return null;
}

function rawMessage(e: unknown): string {
  const errors = graphQlErrors(e);
  if (errors.length > 0) {
    return errors.map((error) => error.message ?? "").join(" · ");
  }
  if (e && typeof e === "object") {
    if (e instanceof Error) return e.message;
    const m = (e as { message?: string }).message;
    if (typeof m === "string") return m;
  }
  return typeof e === "string" ? e : "";
}

function isValidationResult(result: ValidationResultLike | null | undefined): boolean {
  return result?.__typename?.startsWith("Validate") ?? false;
}

export function validationResultMessage(
  result: ValidationResultLike | null | undefined,
  fallback: string,
): string {
  if (!isValidationResult(result)) return fallback;

  const messages =
    result?.issues
      ?.map((issue) => issue.message?.trim() || issue.error?.trim() || issue.field?.trim())
      .filter((message): message is string => Boolean(message)) ?? [];

  return messages.length > 0 ? messages.join("\n") : fallback;
}

export function throwForValidationResult(
  result: ValidationResultLike | null | undefined,
  fallback: string,
): never {
  throw new Error(validationResultMessage(result, fallback));
}

function looksLikeDuplicate(lower: string): boolean {
  return (
    lower.includes("duplicate") ||
    lower.includes("unique") ||
    lower.includes("already exists") ||
    lower.includes("bereits vorhanden")
  );
}

// `duplicateMessage` (i18n-resolved by the caller) is shown when the backend
// error looks like a unique-name violation; without it we fall back to the
// generic `fallback`. Both strings come pre-localized from the call site.
export function userFacingGraphqlError(
  error: unknown,
  fallback: string,
  duplicateMessage?: string,
): string {
  const message = rawMessage(error).trim();
  if (!message) return fallback;

  const lower = message.toLowerCase();
  if (looksLikeDuplicate(lower)) {
    return duplicateMessage ?? fallback;
  }

  if (NON_NULL_BUBBLE_HINTS.some((hint) => lower.includes(hint))) {
    return fallback;
  }

  return message;
}
import { CombinedGraphQLErrors } from "@apollo/client/errors";
